// useRingCentralWebPhone.js
import { useCallback, useRef, useState } from "react";
import { ringCentralSipInfo } from "../api/apiManager";
// import { ringCentralWebPhoneService } from "./ringCentralWebPhoneService";
import ringCentralWebPhoneService from "../ringcentral/ringCentralWebPhoneService";
export const useRingCentralWebPhone = () => {
  const [isReady, setIsReady] = useState(
    ringCentralWebPhoneService.getState().isReady,
  );
  const [isConnecting, setIsConnecting] = useState(
    ringCentralWebPhoneService.getState().isConnecting,
  );
  const [error, setError] = useState(
    ringCentralWebPhoneService.getState().lastError,
  );

  // Prevent double-init in React StrictMode
  const initializingRef = useRef(false);

  const initialize = useCallback(async ({ appUserId } = {}) => {
    if (!appUserId) throw new Error("RingCentral app user id is required");

    if (ringCentralWebPhoneService.getState().isReady) {
      setIsReady(true);
      return true;
    }

    if (initializingRef.current) return false;
    initializingRef.current = true;

    setIsConnecting(true);
    setError("");

    try {
      const response = await ringCentralSipInfo(appUserId);

      //  Log the full response so you can see what's coming back
      console.log("[RC] sip-info raw response:", response);
      console.log("[RC] sip-info response.data:", response?.data);

      //  Handle non-200 with actual backend error message
      if (!response || response.status !== 200) {
        const backendError =
          response?.data?.error ||
          response?.data?.message ||
          response?.data?.title || // .NET ProblemDetails
          JSON.stringify(response?.data) || // show raw if nothing else
          `SIP info request failed with HTTP ${response?.status}`;

        throw new Error(backendError);
      }

      // ✅ Your backend returns: { success, appUserId, deviceId, clientId, sipInfo }
      // No need for data/result unwrapping — it's flat
      const payload = response.data;

      if (!payload?.sipInfo) {
        throw new Error(
          `SIP info missing in response. Got: ${JSON.stringify(payload)}`,
        );
      }

      console.log("[RC] Passing to WebPhone init:", {
        clientId: payload.clientId,
        deviceId: payload.deviceId,
        sipInfo: {
          transport: payload.sipInfo?.transport,
          domain: payload.sipInfo?.domain,
          username: payload.sipInfo?.username,
          outboundProxy: payload.sipInfo?.outboundProxy,
        },
      });

      const ok = await ringCentralWebPhoneService.init({
        ...payload,
        appUserId,
      });

      setIsReady(ok);
      return ok;
    } catch (initError) {
      const message =
        initError?.message || "Failed to initialize RingCentral WebPhone";
      console.error("[RC] initialize error:", message);
      setError(message);
      initializingRef.current = false; // allow retry
      throw new Error(message);
    } finally {
      setIsConnecting(false);
    }
  }, []);

  const ensureReady = useCallback(
    async ({ appUserId } = {}) => initialize({ appUserId }),
    [initialize],
  );

  const placeCall = useCallback(
    async ({ appUserId, to, callerId } = {}) => {
      await ensureReady({ appUserId });
      return ringCentralWebPhoneService.call(to, callerId);
    },
    [ensureReady],
  );

  // const sendDtmf = useCallback(
  //   (digit) => ringCentralWebPhoneService.sendDtmf(digit),
  //   [],
  // );
  const sendDtmf = useCallback(
    (digit) => ringCentralWebPhoneService.sendDTMF(digit),
    [],
  );
  const hangup = useCallback(() => ringCentralWebPhoneService.hangup(), []);
  const mute = useCallback(
    (muted) => ringCentralWebPhoneService.mute(muted),
    [],
  );
  const hold = useCallback((held) => ringCentralWebPhoneService.hold(held), []);

  return {
    isReady,
    isConnecting,
    error,
    initialize,
    ensureReady,
    placeCall,
    sendDtmf,
    hangup,
    mute,
    hold,
    session: ringCentralWebPhoneService.getCurrentSession(),
    service: ringCentralWebPhoneService,
  };
};
